import { forwardRef } from 'react';

import * as SwitchPrimitive from '@radix-ui/react-switch';

import { cn } from '../../lib/cn';

export interface SwitchProps extends Omit<SwitchPrimitive.SwitchProps, 'onChange'> {
  label?: string;
  onChange?: (checked: boolean) => void;
}

export const Switch = forwardRef<HTMLButtonElement, SwitchProps>(function Switch(
  { label, checked, onChange, className, disabled, id, ...rest },
  ref,
) {
  return (
    <div className="flex items-center gap-2">
      <SwitchPrimitive.Root
        ref={ref}
        id={id}
        checked={checked}
        onCheckedChange={(value) => onChange?.(value)}
        disabled={disabled}
        className={cn(
          'relative inline-flex h-5 w-9 shrink-0 cursor-pointer items-center rounded-full border-2 border-transparent',
          'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2',
          'disabled:cursor-not-allowed disabled:opacity-50',
          // No escuro o trilho apagado sobe para o cinza neutro claro: os tons
          // de borda do tema ficam a menos de 1.4:1 da superfície do card.
          'data-[state=checked]:bg-primary-500 data-[state=unchecked]:bg-slate-300',
          'dark:data-[state=unchecked]:bg-slate-400',
          className,
        )}
        {...rest}
      >
        <SwitchPrimitive.Thumb
          className={cn(
            // `bg-white/100` em vez de `bg-white`: o utilitário puro é repintado
            // para o tom do card no escuro, o que apagaria a bolinha; a forma com
            // alfa mantém o branco nos dois temas.
            'pointer-events-none block h-4 w-4 rounded-full bg-white/100 shadow-sm ring-0 transition-transform',
            'data-[state=checked]:translate-x-4 data-[state=unchecked]:translate-x-0',
          )}
        />
      </SwitchPrimitive.Root>
      {label && (
        <label
          htmlFor={id}
          className={cn(
            'text-sm font-body text-slate-700 select-none cursor-pointer',
            disabled && 'cursor-not-allowed opacity-50',
          )}
        >
          {label}
        </label>
      )}
    </div>
  );
});
